// src/components/HRComponents/PolicyCategoryManager.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { Plus, Trash2, Edit2, Check, FolderOpen } from 'lucide-react';
import { toast } from 'react-toastify';

import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import SelectDropdown from '../../customStyles/SelectDropdown';
import DeleteConfirmationOffcanvas from '../../helpers/DeleteConfirmationOffcanvas';

const DOCUMENT_TYPE_OPTIONS = [
  { label: 'Both (Letters & Policies)', value: 'both' },
  { label: 'Policies Only', value: 'policy' },
  { label: 'Letters Only', value: 'letter' },
];

const PolicyCategoryManager = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // States for creating/editing categories
  const [name, setName] = useState('');
  const [documentType, setDocumentType] = useState('policy');
  const [editingId, setEditingId] = useState(null);

  // States for native Delete Offcanvas Modal
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(SummaryApi.getPolicyCategories.url);
      if (res.data.success) {
        setCategories(res.data.data || []);
      }
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // Handle Create or Update submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Category name is required');

    setSubmitting(true);
    try {
      if (editingId) {
        // Update existing category
        await api.put(SummaryApi.updatePolicyCategory.url(editingId), { name, documentType });
        toast.success('Category updated successfully');
      } else {
        // Create new category
        await api.post(SummaryApi.createPolicyCategory.url, { name, documentType });
        toast.success('Category created successfully');
      }
      setName('');
      setDocumentType('policy');
      setEditingId(null);
      fetchCategories();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save category');
    } finally {
      setSubmitting(false);
    }
  };

  // Populate form fields for editing
  const startEdit = (cat) => {
    setEditingId(cat._id);
    setName(cat.name);
    setDocumentType(cat.documentType);
  };

  // Cancel active edit session
  const cancelEdit = () => {
    setEditingId(null);
    setName('');
    setDocumentType('policy');
  };

  // Trigger and open delete offcanvas modal
  const openDeleteModal = (cat) => {
    setCategoryToDelete(cat);
    setIsDeleteOpen(true);
  };

  // Close delete offcanvas modal
  const closeDeleteModal = () => {
    setIsDeleteOpen(false);
    setCategoryToDelete(null);
  };

  // Confirm soft-delete inside the offcanvas modal
  const confirmDelete = async () => {
    if (!categoryToDelete) return;

    setDeleteLoading(true);
    try {
      await api.delete(SummaryApi.deletePolicyCategory.url(categoryToDelete._id));
      toast.success('Category deleted successfully');
      closeDeleteModal();
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="p-4">
      {/* Header section with back button */}
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="rounded-lg bg-red-50 border border-red-100 hover:border-primary text-textPrimary hover:text-primary p-1.5 sm:p-2 transition-colors"
        >
          <FaArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
        <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Manage Policy Categories</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left Side: Create / Edit Form Card */}
        <div className="md:col-span-1">
          <form onSubmit={handleSubmit} className="bg-white rounded-xl border p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-800">
              {editingId ? 'Edit Category' : 'Create New Category'}
            </h2>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Category Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Health & Safety, IT"
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none"
              />
            </div>

            <SelectDropdown
              label="Document Type Limit"
              options={DOCUMENT_TYPE_OPTIONS}
              value={documentType}
              onChange={setDocumentType}
              valueKey="value"
              labelKey="label"
            />

            <div className="flex gap-2 pt-2">
              {editingId && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-2 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-1"
              >
                {editingId ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {submitting ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>

        {/* Right Side: Categories List Card */}
        <div className="md:col-span-2">
          <div className="bg-white rounded-xl border p-5">
            <h2 className="text-sm font-semibold text-gray-800 mb-4">Existing Categories</h2>

            {loading ? (
              <div className="text-center py-8 text-gray-400 text-sm">Loading categories...</div>
            ) : categories.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <FolderOpen className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No categories found. Create one on the left!</p>
              </div>
            ) : (
              <div className="divide-y">
                {categories.map((cat) => (
                  <div key={cat._id} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">{cat.name}</h4>
                      <p className="text-xs text-gray-500 capitalize">
                        Type: {cat.documentType === 'both' ? 'Letters & Policies' : cat.documentType}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => startEdit(cat)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors"
                        title="Edit Category"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openDeleteModal(cat)} // Open the native delete offcanvas modal
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete Category"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cohesive Native Delete Confirmation Offcanvas Modal Integration */}
      <DeleteConfirmationOffcanvas
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Delete Policy Category"
        itemName={categoryToDelete?.name || ''}
        isLoading={deleteLoading}
        deleteType="soft" // Backend implements Soft Delete (isActive: false)
      />
    </div>
  );
};

export default PolicyCategoryManager;